import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import {
    BuildingOfficeIcon,
    UserIcon,
    PhoneIcon,
    EnvelopeIcon,
    CalendarIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    EyeIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';

interface Inquiry {
    _id: string;
    companyName: string;
    contactPersonName: string;
    email: string;
    phone: string;
    industry?: string;
    companySize?: string;
    website?: string;
    description: string;
    requirements?: string;
    status: 'pending' | 'contacted' | 'in-progress' | 'approved' | 'rejected';
    adminNotes?: string;
    contactedBy?: string;
    contactedAt?: string;
    approvedBy?: string;
    approvedAt?: string;
    companyId?: string;
    createdAt: string;
    updatedAt: string;
}

interface PaginationInfo {
    currentPage: number;
    limit: number;
    pages: number;
    total: number;
}

const Inquiries: React.FC = () => {
    const dispatch = useAppDispatch();
    const [inquiries, setInquiries] = useState<Inquiry[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [pagination, setPagination] = useState<PaginationInfo>({
        currentPage: 1,
        limit: 10,
        pages: 1,
        total: 0
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const fetchInquiries = async (page = 1, limit = 10, search = '', status = 'all') => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });

            if (search) {
                params.append('q', search);
            }

            if (status !== 'all') {
                params.append('status', status);
            }

            const response = await api.get(`/inquiries?${params.toString()}`);
            const data = response.data;

            if (data.success) {
                setInquiries(data.data || []);
                setPagination({
                    currentPage: data.pagination?.currentPage || 1,
                    limit: data.pagination?.limit || 10,
                    pages: data.pagination?.pages || 1,
                    total: data.pagination?.total || 0
                });
            } else {
                setInquiries([]);
                setPagination({
                    currentPage: 1,
                    limit: 10,
                    pages: 1,
                    total: 0
                });
            }
        } catch (error: any) {
            console.error('Fetch inquiries error:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch inquiries',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInquiries(currentPage, itemsPerPage, searchTerm, statusFilter);
    }, [currentPage, itemsPerPage]);

    // Debounced search effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setCurrentPage(1);
            fetchInquiries(1, itemsPerPage, searchTerm, statusFilter);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, statusFilter]);

    const handleStatusUpdate = async (inquiryId: string, newStatus: string) => {
        try {
            let endpoint = '';
            let payload = {};

            switch (newStatus) {
                case 'contacted':
                    endpoint = `/inquiries/${inquiryId}/contact`;
                    payload = { notes: 'Marked as contacted' };
                    break;
                case 'approved':
                    endpoint = `/inquiries/${inquiryId}/approve`;
                    payload = {
                        password: 'TempPassword123!',
                        companyDetails: {
                            industry: 'Technology',
                            companySize: '11-50'
                        }
                    };
                    break;
                case 'rejected':
                    endpoint = `/inquiries/${inquiryId}/reject`;
                    payload = { reason: 'Inquiry rejected' };
                    break;
                default:
                    return;
            }

            await api.put(endpoint, payload);
            dispatch(showToast({
                message: `Inquiry ${newStatus} successfully`,
                type: 'success'
            }));
            fetchInquiries(currentPage, itemsPerPage, searchTerm, statusFilter);
        } catch (error: any) {
            console.error('Status update error:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to update inquiry status',
                type: 'error'
            }));
        }
    };

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', icon: ClockIcon },
            contacted: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', icon: PhoneIcon },
            'in-progress': { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', icon: ClockIcon },
            approved: { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: CheckCircleIcon },
            rejected: { color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', icon: XCircleIcon }
        };

        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
        const Icon = config.icon;

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                <Icon className="h-3 w-3 mr-1" />
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (newLimit: number) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
                <div>
                    <h1 className="text-3xl font-bold mb-2">
                        Company Inquiries
                    </h1>
                    <p className="text-blue-100">
                        Manage company inquiries and partnership requests
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search inquiries..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                    </div>
                    <div className="sm:w-48">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        >
                            <option value="all">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="contacted">Contacted</option>
                            <option value="in-progress">In Progress</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Inquiries List */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            All Inquiries ({pagination.total})
                        </h2>
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Items per page:</span>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {inquiries.length === 0 ? (
                        <div className="p-8 text-center">
                            <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500 dark:text-gray-400">No inquiries found</p>
                        </div>
                    ) : (
                        inquiries.map((inquiry) => (
                            <div key={inquiry._id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {inquiry.companyName}
                                            </h3>
                                            {getStatusBadge(inquiry.status)}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            <div className="flex items-center space-x-2">
                                                <UserIcon className="h-4 w-4 text-gray-400" />
                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                    {inquiry.contactPersonName}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                    {inquiry.email}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <PhoneIcon className="h-4 w-4 text-gray-400" />
                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                    {inquiry.phone}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <CalendarIcon className="h-4 w-4 text-gray-400" />
                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                    {formatDate(inquiry.createdAt)}
                                                </span>
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                            {inquiry.description}
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-2 ml-4">
                                        <button
                                            onClick={() => {
                                                setSelectedInquiry(inquiry);
                                                setShowModal(true);
                                            }}
                                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                            title="View Details"
                                        >
                                            <EyeIcon className="h-5 w-5" />
                                        </button>
                                        {inquiry.status === 'pending' && (
                                            <div className="flex space-x-1">
                                                <button
                                                    onClick={() => handleStatusUpdate(inquiry._id, 'contacted')}
                                                    className="px-3 py-1 text-xs bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800 rounded-full transition-colors"
                                                >
                                                    Contact
                                                </button>
                                                <button
                                                    onClick={() => handleStatusUpdate(inquiry._id, 'approved')}
                                                    className="px-3 py-1 text-xs bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800 rounded-full transition-colors"
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => handleStatusUpdate(inquiry._id, 'rejected')}
                                                    className="px-3 py-1 text-xs bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800 rounded-full transition-colors"
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.total)} of {pagination.total} results
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeftIcon className="h-4 w-4" />
                                </button>

                                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                                    const startPage = Math.max(1, pagination.currentPage - 2);
                                    const page = startPage + i;
                                    if (page > pagination.pages) return null;

                                    return (
                                        <button
                                            key={page}
                                            onClick={() => handlePageChange(page)}
                                            className={`px-3 py-2 rounded-lg text-sm font-medium ${page === pagination.currentPage
                                                ? 'bg-blue-600 text-white'
                                                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                }`}
                                        >
                                            {page}
                                        </button>
                                    );
                                })}

                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.pages}
                                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronRightIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Inquiry Details Modal */}
            {showModal && selectedInquiry && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Inquiry Details
                                </h3>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <XCircleIcon className="h-6 w-6" />
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="space-y-6">
                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        {selectedInquiry.companyName}
                                    </h4>
                                    {getStatusBadge(selectedInquiry.status)}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Contact Person
                                        </label>
                                        <p className="text-sm text-gray-900 dark:text-white">
                                            {selectedInquiry.contactPersonName}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Email
                                        </label>
                                        <p className="text-sm text-gray-900 dark:text-white">
                                            {selectedInquiry.email}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Phone
                                        </label>
                                        <p className="text-sm text-gray-900 dark:text-white">
                                            {selectedInquiry.phone}
                                        </p>
                                    </div>
                                    {selectedInquiry.website && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Website
                                            </label>
                                            <p className="text-sm text-blue-600 dark:text-blue-400">
                                                <a href={selectedInquiry.website} target="_blank" rel="noopener noreferrer">
                                                    {selectedInquiry.website}
                                                </a>
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Description
                                    </label>
                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                                            {selectedInquiry.description}
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Submitted
                                        </label>
                                        <p className="text-gray-900 dark:text-white">
                                            {formatDate(selectedInquiry.createdAt)}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Last Updated
                                        </label>
                                        <p className="text-gray-900 dark:text-white">
                                            {formatDate(selectedInquiry.updatedAt)}
                                        </p>
                                    </div>
                                </div>

                                {selectedInquiry.status === 'pending' && (
                                    <div className="flex space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <button
                                            onClick={() => {
                                                handleStatusUpdate(selectedInquiry._id, 'contacted');
                                                setShowModal(false);
                                            }}
                                            className="btn-primary"
                                        >
                                            Mark as Contacted
                                        </button>
                                        <button
                                            onClick={() => {
                                                handleStatusUpdate(selectedInquiry._id, 'approved');
                                                setShowModal(false);
                                            }}
                                            className="btn-success"
                                        >
                                            Approve Inquiry
                                        </button>
                                        <button
                                            onClick={() => {
                                                handleStatusUpdate(selectedInquiry._id, 'rejected');
                                                setShowModal(false);
                                            }}
                                            className="btn-danger"
                                        >
                                            Reject Inquiry
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Inquiries;
